import { Box, Flex, Text, Button } from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import type { AccountInfo } from "@azure/msal-browser";
import { useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../../../authConfig";
import { postLoginSSO } from "../../../_services/auth.service";
import useLoginToken from "../../../store/login.store";
import { useCookies } from "react-cookie";
import { ILoginResponse } from "../../../_services/interface/login.interface";
import { SimpleFormError } from "../../../components/login/formError.component";
import { IErrorResponse } from "../../../_services/interface/IDefaults";
import { logAuthDiag, tokenCookieOptions } from "../../../_services/authClient";
// GEN-3167: o SSO tem que respeitar o destino do link igual ao login por senha.
import { destinoAposLogin } from "../../../_services/returnTo";
import { contaParaLogin } from "./contaParaLogin";
import { clearSsoClient } from "../../../_services/ssoRelogin";

export const LoginFormSSO = () => {
  const [cookies, setCookie] = useCookies(["token"]);
  const { setToken } = useLoginToken();
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();
  const [smplErrorMessage, setSmplErrorMessage] = useState<string>();
  // Clicar de novo no botão com o popup pendente sobrescrevia o `nonce` guardado num
  // state à parte — o token que voltasse do PRIMEIRO popup ia para o backend com o nonce do SEGUNDO
  // clique, e o backend recusava (nonce não bate com o do ID token). Duas defesas: (1) o botão fica
  // desabilitado enquanto um popup está em andamento; (2) o idToken/nonce/conta do resultado nascem
  // JUNTOS, num único state, então mesmo que a guarda (1) falhe não existe uma janela onde um nonce
  // "solto" possa ser sobrescrito por outro popup antes de ser lido.
  const [loginPending, setLoginPending] = useState(false);
  // GEN-3604 (F0.1): o ID token do Entra vai para o backend, que o valida de verdade (assinatura,
  // emissor, audiência, expiração, replay). O checksum abaixo fica só como transição.
  // A conta é a que a pessoa escolheu NESSE popup — é dela o `idToken`, então
  // e-mail/nome/checksum têm que ser dela também (com 2+ contas em cache, `accounts[0]` podia ser
  // outra pessoa).
  const [ssoResult, setSsoResult] = useState<
    { idToken: string; nonce: string; account: AccountInfo; seq: number } | undefined
  >();
  // `loginPending` precisa cobrir o popup INTEIRO até a troca do token com o
  // backend (`postLoginSSO`), não só a espera do popup — ver os dois refs abaixo.
  const attemptSeq = useRef(0);
  const consumedSeq = useRef(0);

  const handleLogin = async (loginType: string) => {
    if (loginType !== "popup" || loginPending) return;
    setLoginPending(true);
    const seq = ++attemptSeq.current;
    // Flag local, não o state `ssoResult` (fica preso ao valor da render em que
    // este closure nasceu — checá-lo aqui reintroduziria o mesmo bug: liberaria o botão antes da
    // troca do token). Marca só se este popup produziu um resultado válido.
    let produziuResultado = false;
    try {
      // GEN-3604 (F0.1): nonce entregue ao MSAL e conferido no backend dentro do ID token.
      const nonce = crypto.randomUUID();
      const isLogged: any = await instance.loginPopup({ ...loginRequest, nonce }).catch((e) => {
        // console.log(e);
      });
      const { account, erro: erroConta } = contaParaLogin(isLogged, accounts);
      // Várias contas Microsoft no navegador e o popup sem dizer qual foi escolhida —
      // antes caía em `accounts[0]` e o backend recusava com erro genérico; agora a tela explica.
      if (erroConta) setSmplErrorMessage(erroConta);
      if (isLogged?.idToken && isLogged?.accessToken && account) {
        // Não libera `loginPending` aqui. Antes, o `finally` liberava o botão assim que o popup
        // fechava, ANTES da troca do token com `postLoginSSO` — um segundo clique podia iniciar
        // outra troca válida em paralelo, e o token mais lento sobrescrevia o mais novo. Quem libera
        // agora é o efeito de troca do token, abaixo, no fim (sucesso ou erro).
        produziuResultado = true;
        setSsoResult({ idToken: isLogged.idToken, nonce, account, seq });
      }
    } finally {
      // Só libera aqui quando este ramo NÃO produziu um `ssoResult` (popup cancelado, erro do MSAL,
      // conta ausente) — nesses casos o efeito de troca do token nunca roda, e ninguém mais liberaria
      // o botão.
      if (!produziuResultado) setLoginPending(false);
    }
  };

  useEffect(() => {
    //Check if the person is already logged and send this person to the dashboard
    if (cookies.token) {
      navigate(destinoAposLogin());
    }
    const handleAuth = async () => {
      if (!ssoResult) return;
      // A checagem de "resultado mais velho que um já consumido" roda ANTES de
      // qualquer trabalho (inclusive o caminho síncrono de conta incompleta) — continua sem mexer em
      // `loginPending` quando perde a corrida, essa responsabilidade é do resultado mais novo.
      if (ssoResult.seq < consumedSeq.current) return;
      consumedSeq.current = ssoResult.seq;
      const conta = ssoResult.account;
      try {
        // Antes, conta sem `name` (dado incompleto vindo do popup do MSAL) não caía
        // em nenhum ramo — nem chamava `postLoginSSO`, nem passava pelo `finally` que soltava
        // `loginPending` — e o botão Microsoft ficava desabilitado para sempre. Agora entra no
        // `finally` incondicional abaixo e mostra um erro visível.
        if (!conta || !conta.username || !conta.name) {
          setSmplErrorMessage("Não foi possível ler os dados da conta Microsoft.");
          setSsoResult(undefined);
          return;
        }
        const applicationId = conta.idTokenClaims?.aud || "TAXLY_123";
        const token = generateToken(conta.username, applicationId);
        const { status, response } = await postLoginSSO({
          email: conta.username,
          name: conta.name,
          clientSideToken: token,
          idToken: ssoResult.idToken,
          nonce: ssoResult.nonce,
        });
        if (status === 200) {
          const theToken = (response as ILoginResponse).accessToken;
          setToken(theToken);
          setCookie("token", theToken, tokenCookieOptions());
          // GEN-4150: sessão pelo MSAL não veio do broker — apaga a origem SSO guardada para o relogin.
          clearSsoClient();
          logAuthDiag("login-sso-azure:after-set-cookie", { tokenLen: theToken?.length ?? 0 });
          navigate(destinoAposLogin());
        } else {
          const err = (response as IErrorResponse).message;
          switch (err) {
            case "LOGIN_USER_NOT_FOUND":
              setSmplErrorMessage("Usuário inválido ou não encontrado");
              break;
            case "LOGIN_USER_INACTIVE":
              setSmplErrorMessage("Usuário inválido ou não encontrado");
              break;
            default:
              // Sem resposta HTTP (status null) e código ECONNABORTED é o timeout de
              // 30s configurado em `postLoginSSO` — mensagem específica em vez do genérico "Algo
              // aconteceu", já que aqui a causa mais provável é rede lenta, não erro do backend.
              setSmplErrorMessage(
                status === null && typeof err === "string" && err.startsWith("ECONNABORTED")
                  ? "A entrada com a conta Microsoft demorou demais. Tente novamente."
                  : "Algo aconteceu. Tente novamente por favor."
              );
              break;
          }
          setSsoResult(undefined);
        }
      } finally {
        // Incondicional — roda no sucesso, no erro do backend E na conta incompleta,
        // que é justamente o caminho que antes escapava do `finally` e travava o botão.
        setLoginPending(false);
      }
    };
    handleAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ssoResult]);

  return (
    <Flex display="flex" fontFamily="Poppins" fontWeight={500} alignItems="center" width={369}>
      <Flex direction="column" w="100%">
        <Text fontSize="32px" textAlign="center">
          Login Statkraft
        </Text>
        <Box marginTop="50px">
          {smplErrorMessage ? <SimpleFormError msg={smplErrorMessage} /> : null}
          <Flex direction="column" gap="16px" alignItems="stretch">
            <Button
              background="#4B4EFF"
              color="white"
              isDisabled={loginPending}
              onClick={() => handleLogin("popup")}
            >
              Entre usando sua conta Microsoft
            </Button>
          </Flex>
        </Box>
      </Flex>
    </Flex>
  );

  function generateToken(email: string, appId: string): number {
    let emailSum = 0;
    let appIdSum = 0;
    for (let i = 0; i < email.length; i++) {
      emailSum += email.charCodeAt(i) * i + 1;
    }
    for (let i = 0; i < appId.length; i++) {
      appIdSum += appId.charCodeAt(i) * i + 1;
    }
    return emailSum * appIdSum;
  }
};
